import React, { useContext } from 'react'
import { myAtSaveAdvertMutation } from '../../components/search-listing-save-advert/query/advert-save-list.fed.query'
import { useSecureFederatedMutation as useMutation } from '@at/sauron-platform-graphql'
import { ApolloError } from '@apollo/client'
import { SavedAdvertsQueryContext } from '../../contexts/saved-adverts-context'
import { useForm } from '../../forms/hocs/form.context'

import { FilterName } from '../../__generated__/fed-graphql-types'

import { useModalLoginContext } from '../../contexts/modal-login-context'
import { useToastContext } from '../../contexts/toast-context'
import { OdsTypeWhiteList, trackOdsAdblockWhitelist } from '@at/sauron-tracking/lib/utils/ods-legacy'
import { IconSavedOn } from '@at/design-system-icons'
import {
    AddOrDeleteSavedAdvertMutation_Mutation,
    AddOrDeleteSavedAdvertMutationVariables,
} from '../../components/search-listing-save-advert/query/__generated__/advert-save-list.fed.query'
import { useATTrackInteraction, useATTrackPrestigeEvent } from '@at/consumer-web-snowplow'
import { useUserState, SignInMode } from '@at/connected-signin'
import { SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext_advertContext_TrackingAdvertContext } from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { advertContext } from '../../utils/tracking/advert-context'
import { Entities } from '@at/track-core'

export const firstSavedAdvertsToastMessage = 'This vehicle has been added to your save list.'
export const savedAdvertsToastMessage = 'Vehicle saved -'

interface SavedAdvertsHook {
    error?: ApolloError
    loading?: boolean
    updateAdvertConnected: (saved: boolean, advertId: string, odsData: OdsTypeWhiteList) => Promise<void>
}

export const useSavedAdvertsContext = (
    trackingAdvertContext?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext_advertContext_TrackingAdvertContext | null
): SavedAdvertsHook => {
    const [userState, updateUser] = useUserState()
    const [addOrDeleteSavedAdvert, { loading, error }] = useMutation<
        AddOrDeleteSavedAdvertMutation_Mutation,
        AddOrDeleteSavedAdvertMutationVariables
    >(myAtSaveAdvertMutation)
    const { savedAdvertsList, setSavedAdvertsList: setSavedAdvertList } = useContext(SavedAdvertsQueryContext)

    const trackingEntities: Entities = trackingAdvertContext?.id ? [advertContext(trackingAdvertContext)] : []
    const trackSaveAVehicle = useATTrackInteraction()
    const trackSaveAVehiclePrestige = useATTrackPrestigeEvent()

    const { selectedMap, sortBySelected } = useForm()
    const addToast = useToastContext()
    const { isShowingModalLogin, handleToggleModalLogin } = useModalLoginContext()

    const saveMutation = async (saved: boolean, advertId: string, odsData: OdsTypeWhiteList) => {
        try {
            const { data } = await addOrDeleteSavedAdvert({
                variables: {
                    addOrDeleteSavedAdvertInput: {
                        advertId: advertId,
                        isSaved: saved,
                        searchReferrerParams: {
                            latLong: selectedMap.get(FilterName.lat_long)?.values[0],
                            postcode: selectedMap.get(FilterName.postcode)?.values[0],
                            radius: selectedMap.get(FilterName.distance)?.values[0],
                            sort: sortBySelected,
                        },
                    },
                },
            })

            const isSaved = data?.addOrDeleteSavedAdvertWithUserCheck.isSaved
            if (isSaved) {
                setSavedAdvertList([...savedAdvertsList, advertId])
                addToast({
                    message: savedAdvertsList.length ? savedAdvertsToastMessage : firstSavedAdvertsToastMessage,
                    icon: <IconSavedOn title="Search saved" fill="red" customSize={{ height: 14, width: 14 }} />,
                    ...(savedAdvertsList.length && {
                        link: '/secure/saved-adverts',
                        linkText: 'View save list',
                    }),
                })
                trackSaveAVehiclePrestige({
                    prestigeEventName: 'save-a-vehicle',
                    customiseEntities: (entities) => [...entities, ...trackingEntities],
                })
                trackSaveAVehicle({
                    trackingLabel: 'save-vehicle',
                    customiseEntities: (entities) => [...entities, ...trackingEntities],
                })
            } else {
                setSavedAdvertList(savedAdvertsList.filter((advert) => advert !== advertId))
            }

            trackOdsAdblockWhitelist({
                ...odsData,
                event: isSaved ? 'SAVE_ADVERT_CLICK' : 'DELETE_SAVED_ADVERT_CLICK',
                source: 'VEHICLE_CLICK',
            })
        } catch (error: unknown) {
            // Handle the error
            const graphQLError = error && (error as ApolloError).graphQLErrors?.[0]?.extensions?.code

            // If session expired we display the login modal again
            if (graphQLError == 'USER_NOT_FOUND') {
                updateUser({ ...userState, userSignedIn: false })
                handleToggleModalLogin({
                    showing: true,
                    signInMode: SignInMode.SavedAdvert,
                    afterSignInCallback: async () => await saveMutation(saved, advertId, odsData),
                })
            } else {
                // Any other server error
                addToast({
                    message: 'Sorry, something went wrong. Please try again later',
                })
            }
        }
    }

    const updateAdvertConnected = async (saved: boolean, advertId: string, odsData: OdsTypeWhiteList) => {
        if (!userState.userSignedIn) {
            /** User not logged in, display modal */
            if (!isShowingModalLogin) {
                handleToggleModalLogin({
                    showing: true,
                    signInMode: SignInMode.SavedAdvert,
                    afterSignInCallback: async () => await saveMutation(saved, advertId, odsData),
                })
            }
            return
        }
        await saveMutation(saved, advertId, odsData)
    }

    return {
        error,
        loading,
        updateAdvertConnected,
    }
}
